import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { fetchLeadsFromN8N } from '../utils/n8nDataFetchers';
import { useAuth } from '../contexts/AuthContext';
import type { MinimalLeadData } from '../types';

/**
 * Hook to fetch leads for a specific campaign on-demand.
 * Only fetches when enabled=true (e.g., when campaign is selected/clicked).
 * This replaces the eager loading of all campaigns' leads on page load.
 */
export const useLeadsForCampaign = (
  campaignId: string | undefined,
  userEmailOverride?: string,
  options?: { enabled?: boolean }
) => {
  const { currentUser, targetEmail } = useAuth();
  const userEmail = userEmailOverride || targetEmail || currentUser?.email;
  const enabled = options?.enabled !== false && !!campaignId && !!userEmail;

  return useQuery<MinimalLeadData[]>({
    queryKey: queryKeys.leads.byCampaign(campaignId || '', userEmail),
    queryFn: () => fetchLeadsFromN8N(campaignId!, userEmail!),
    enabled,
    // Leads change only through enrichment (slow, server-side). With the
    // persisted cache (QueryProvider), a refresh serves yesterday's leads
    // from localStorage INSTANTLY and revalidates in the background — no
    // blocking proxy round-trip, no re-transform of every lead on load.
    // Status flips (ISR edits, outreach actions) already write straight into
    // this cache via syncLeadStatusInCampaignCache, so staleness is bounded
    // to fields only n8n can change.
    refetchOnMount: true,
    // 2 minutes — matches useCampaigns so re-opening a campaign never shows
    // leads older than the campaign list itself. leads_cards now rides the
    // fast Core redirect, so this revalidation is cheap. Status edits made
    // locally stay instant (written straight into this cache by
    // syncLeadStatusInCampaignCache / useISRLeadMutation), this window only
    // bounds changes that arrive from the server side (enrichment, teammates).
    staleTime: 2 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000, // 24h — persisted entries stay usable across sessions
    refetchOnWindowFocus: false,
    // NO placeholderData — showing stale data while re-fetching is what causes
    // the "must reload to see updates" feel. The skeleton flash on navigation
    // is a tiny window (<1s) and signals the user that the data is loading.
  });
};
